import React, { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import parse from "html-react-parser";
import {
  getSeoPagesByCategorySlug,
  seoImageUrl,
} from "../../app/seopages/seopages.services";
import { truncate } from "../../Utils/html";
import Seo from "../../Seo/Seo";
import { webPageSchema, itemListSchema } from "../../Seo/jsonld";

/**
 * Listing page for a group of admin-managed SEO landing pages,
 * at /seo-category/:categorySlug.
 *
 * Uses the single `seo-pages/category/:slug` endpoint, which returns the
 * category and its pages together, so one request covers both the heading and
 * the list. The list items are real <NavLink>s, which is what lets crawlers
 * reach the individual landing pages from here.
 */
export default function SeoCategory() {
  const { categorySlug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);
    setState("loading");

    getSeoPagesByCategorySlug(categorySlug)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setState(result ? "ready" : "missing");
      })
      .catch(() => {
        if (!cancelled) setState("missing");
      });

    return () => {
      cancelled = true;
    };
  }, [categorySlug]);

  const path = `/seo-category/${categorySlug}`;

  if (state === "missing") {
    return (
      <main>
        <Seo
          title="Kategorien blev ikke fundet | Onlineskolemad"
          description="Kategorien findes ikke."
          path={path}
          index={false}
        />
        <section className="sec-pd rel-z-1">
          <div className="container">
            <div className="title-1 title-center">
              <h1>Kategorien blev ikke fundet</h1>
              <p>Vi kunne ikke finde den kategori, du leder efter.</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const categoryName = data?.category?.categoryName || "";
  const pages = data?.seoPages || [];
  const description = truncate(data?.category?.description || "");


  return (
    <main>
      <Seo
        title={categoryName ? `${categoryName} | Onlineskolemad` : undefined}
        description={
          description ||
          (categoryName
            ? `Se alle sider under ${categoryName} hos Onlineskolemad.`
            : undefined)
        }
        path={path}
        index={state === "ready"}
        jsonLd={
          state === "ready"
            ? [
                webPageSchema({
                  title: categoryName,
                  description,
                  url: path,
                }),
                itemListSchema({
                  name: categoryName,
                  url: path,
                  items: pages.map((item) => ({
                    name: item.title,
                    path: `/seo-pages/${item.slug}`,
                  })),
                }),
              ]
            : undefined
        }
      />

      <section className="page-info-sec">
        <div
          className="bg-fixed wow fadeIn"
          data-wow-delay="0.1s"
          style={{ background: 'url("/assets/img/page-info1.png")' }}
        />
        <div className="bg-fixed img-overlay wow fadeIn" data-wow-delay="0.2s" />
        <div className="container">
          <div className="row align-items-center justify-content-center">
            <div className="col-md-12">
              <div className="page-info-block wow fadeInUp" data-wow-delay="0.3s">
                <h1>{categoryName}</h1>
                <div className="btm-line-2 text-center justify-content-center">
                  <span />
                  <span />
                </div>
                {description && <p>{description}</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sec-pd bg-gray">
        <div className="container">
          {data?.category?.description && (
            <div className="row">
              <div className="col-md-12">
                <div className="title-1">{parse(data.category.description)}</div>
              </div>
            </div>
          )}
          <div className="row wow fadeInUp">
            {pages.map((item) => {
              const image = seoImageUrl(item.imageBanner);
              return (
                <div className="col-lg-4 col-md-6 col-sm-6 d-flx" key={item.id}>
                  <div className="menu-card">
                    {image && (
                      <div className="menu-card-img">
                        <img src={image} alt={item.title} loading="lazy" />
                      </div>
                    )}
                    <div className="menu-desc">
                      <div className="menu-desc-head">
                        <h5>{item.title}</h5>
                      </div>
                      <p>{truncate(item.description1 || "", 140)}</p>
                      <NavLink className="btn btn-1" to={`/seo-pages/${item.slug}`}>
                        Læs om {item.title}
                      </NavLink>
                    </div>
                  </div>
                </div>
              );
            })}
            {pages.length === 0 && state === "ready" && (
              <div className="col-md-12">
                <p>Der er endnu ingen sider i denne kategori.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
